class SolenaCollection extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.mobileQuery = window.matchMedia('(max-width: 760px)');
    this.filterButton = this.querySelector('[data-scp-filter]');
    this.sidebar = this.querySelector('[data-scp-sidebar]');
    this.closeButton = this.querySelector('[data-scp-close]');
    this.status = this.querySelector('[data-scp-status]');
    this.addRequests = new Map();
    this.resetTimers = new Set();
    this.dataset.filtersOpen = 'false';

    const { signal } = this.abortController;
    this.addEventListener('click', (event) => this.onClick(event), { signal });
    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    this.mobileQuery.addEventListener('change', () => this.syncViewport(), { signal });
    this.syncViewport();
  }

  disconnectedCallback() {
    this.dataset.filtersOpen = 'false';
    this.updateBodyLock();
    this.abortController?.abort();
    this.addRequests?.forEach((controller) => controller.abort());
    this.resetTimers?.forEach((timer) => window.clearTimeout(timer));
    this.abortController = null;
  }

  onClick(event) {
    const openButton = event.target.closest('[data-scp-filter]');
    const closeButton = event.target.closest('[data-scp-close]');
    const addButton = event.target.closest('[data-scp-add]');

    if (openButton && this.contains(openButton)) {
      this.openFilters();
      return;
    }

    if (closeButton && this.contains(closeButton)) {
      this.closeFilters();
      return;
    }

    if (event.target === this.sidebar && this.filtersAreOpen()) {
      this.closeFilters();
      return;
    }

    if (addButton && this.contains(addButton)) this.addToCart(addButton);
  }

  onKeydown(event) {
    if (!this.filtersAreOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeFilters();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      this.closeButton?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  openFilters() {
    if (!this.mobileQuery.matches || !this.sidebar) return;

    this.previouslyFocused = document.activeElement;
    this.dataset.filtersOpen = 'true';
    this.sidebar.classList.add('open');
    this.sidebar.setAttribute('aria-hidden', 'false');
    this.filterButton?.setAttribute('aria-expanded', 'true');
    this.updateBodyLock();
    window.requestAnimationFrame(() => this.closeButton?.focus());
  }

  closeFilters({ restoreFocus = true } = {}) {
    this.dataset.filtersOpen = 'false';
    this.sidebar?.classList.remove('open');
    this.filterButton?.setAttribute('aria-expanded', 'false');
    if (this.mobileQuery.matches) this.sidebar?.setAttribute('aria-hidden', 'true');
    this.updateBodyLock();

    if (restoreFocus && this.previouslyFocused?.isConnected) this.previouslyFocused.focus();
  }

  filtersAreOpen() {
    return this.dataset.filtersOpen === 'true';
  }

  syncViewport() {
    if (!this.sidebar) return;

    if (this.mobileQuery.matches) {
      this.sidebar.setAttribute('role', 'dialog');
      this.sidebar.setAttribute('aria-modal', 'true');
      this.sidebar.setAttribute('aria-labelledby', this.sidebar.dataset.dialogTitle);
      this.sidebar.setAttribute('aria-hidden', String(!this.filtersAreOpen()));
    } else {
      this.closeFilters({ restoreFocus: false });
      this.sidebar.removeAttribute('role');
      this.sidebar.removeAttribute('aria-modal');
      this.sidebar.removeAttribute('aria-labelledby');
      this.sidebar.removeAttribute('aria-hidden');
    }
  }

  getFocusableElements() {
    if (!this.sidebar) return [];

    return [...this.sidebar.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => !element.closest('[hidden]'));
  }

  updateBodyLock() {
    const hasOpenDrawer = Boolean(document.querySelector('solena-collection[data-filters-open="true"]'));
    document.body.classList.toggle('scp-lock', hasOpenDrawer);
  }

  async addToCart(button) {
    if (button.disabled || this.addRequests.has(button)) return;

    const idleLabel = button.textContent.trim();
    const requestController = new AbortController();
    this.addRequests.set(button, requestController);
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = button.dataset.adding || idleLabel;

    try {
      const addResponse = await fetch(this.dataset.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(button.dataset.scpAdd), quantity: 1 }),
        signal: requestController.signal
      });
      if (!addResponse.ok) throw new Error('Cart add request failed');

      const cartResponse = await fetch(this.dataset.cartUrl, {
        headers: { Accept: 'application/json' },
        signal: requestController.signal
      });
      if (!cartResponse.ok) throw new Error('Cart request failed');

      const cart = await cartResponse.json();
      this.updateCartBadge(cart.item_count);
      button.classList.add('scp-added');
      button.textContent = button.dataset.added || idleLabel;
      this.announce(`${button.dataset.productTitle}: ${button.textContent}`);

      const timer = window.setTimeout(() => {
        button.classList.remove('scp-added');
        button.textContent = idleLabel;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        this.resetTimers.delete(timer);
      }, 2000);
      this.resetTimers.add(timer);
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.announce(this.dataset.cartErrorLabel);
      if (button.dataset.url) {
        window.location.assign(button.dataset.url);
      } else {
        button.textContent = idleLabel;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    } finally {
      this.addRequests.delete(button);
    }
  }

  announce(message) {
    if (!this.status) return;
    this.status.textContent = '';
    window.requestAnimationFrame(() => {
      this.status.textContent = message;
    });
  }

  updateCartBadge(itemCount) {
    document.querySelectorAll('.snt-cart').forEach((cartLink) => {
      let badge = cartLink.querySelector('em');

      if (itemCount > 0) {
        if (!badge) {
          badge = document.createElement('em');
          cartLink.appendChild(badge);
        }
        badge.textContent = itemCount;
      } else {
        badge?.remove();
      }
    });
  }
}

if (!customElements.get('solena-collection')) {
  customElements.define('solena-collection', SolenaCollection);
}
